"use client";

import { focusManager } from "@tanstack/react-query";
import { discoveryConfig } from "./config";
import {
  flushDiscoveryImpressions,
  type ImpressionMetadata,
  recordQualifiedImpression,
} from "./impression-client";

type Observation = {
  metadata: ImpressionMetadata;
  ratio: number;
  qualified?: boolean;
  timer?: ReturnType<typeof setTimeout>;
};
const observations = new Map<Element, Observation>();
let observer: IntersectionObserver | undefined;
let mutation: MutationObserver | undefined;
let unsubscribe: (() => void) | undefined;

function suppressed(element: Element) {
  if (
    document.visibilityState === "hidden" ||
    !focusManager.isFocused() ||
    !element.isConnected
  )
    return true;
  if (
    element.closest(
      '[hidden], [inert], [aria-hidden="true"], [data-state="inactive"]',
    )
  )
    return true;
  const style = getComputedStyle(element);
  if (
    style.display === "none" ||
    style.visibility === "hidden" ||
    style.opacity === "0"
  )
    return true;
  return [
    ...document.querySelectorAll<HTMLElement>(
      '[data-slot="drawer-overlay"][data-state="open"], [data-slot="dialog-overlay"][data-state="open"], [role="dialog"][aria-modal="true"]',
    ),
  ].some((overlay) => {
    const overlayStyle = getComputedStyle(overlay);
    return (
      overlay.isConnected &&
      overlayStyle.display !== "none" &&
      overlayStyle.visibility !== "hidden"
    );
  });
}

function evaluate(element: Element, state: Observation) {
  if (state.qualified) return;
  if (
    state.ratio < discoveryConfig.impressionVisibleRatio ||
    suppressed(element)
  ) {
    clearTimeout(state.timer);
    state.timer = undefined;
    return;
  }
  if (state.timer) return;
  state.timer = setTimeout(() => {
    state.timer = undefined;
    if (
      state.ratio >= discoveryConfig.impressionVisibleRatio &&
      !suppressed(element)
    ) {
      state.qualified = true;
      recordQualifiedImpression(state.metadata);
    }
  }, discoveryConfig.impressionDwellMs);
}
function update() {
  for (const [element, state] of observations) evaluate(element, state);
}

export function observeDiscoveryImpression(
  element: Element,
  metadata: ImpressionMetadata,
): () => void {
  if (typeof IntersectionObserver === "undefined") return () => {};
  if (!observer) {
    observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const state = observations.get(entry.target);
          if (!state) continue;
          state.ratio = entry.isIntersecting ? entry.intersectionRatio : 0;
          evaluate(entry.target, state);
        }
      },
      {
        threshold: [0, discoveryConfig.impressionVisibleRatio, 1],
        rootMargin: "0px",
      },
    );
    mutation = new MutationObserver(update);
    mutation.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: [
        "hidden",
        "inert",
        "aria-hidden",
        "data-state",
        "class",
        "style",
      ],
    });
    unsubscribe = focusManager.subscribe(update);
    document.addEventListener("visibilitychange", update);
    window.addEventListener("pagehide", flushDiscoveryImpressions);
  }
  const state: Observation = { metadata, ratio: 0 };
  observations.set(element, state);
  observer.observe(element);
  return () => {
    clearTimeout(state.timer);
    observations.delete(element);
    observer?.unobserve(element);
    if (!observations.size) {
      observer?.disconnect();
      observer = undefined;
      mutation?.disconnect();
      mutation = undefined;
      unsubscribe?.();
      unsubscribe = undefined;
      document.removeEventListener("visibilitychange", update);
      window.removeEventListener("pagehide", flushDiscoveryImpressions);
      // Navigation flush retains true impressions after their cards unmount.
      flushDiscoveryImpressions();
    }
  };
}
